'use client';

import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslations } from 'use-intl';
import Button from '@/components/atoms/button';
import { ColumnMappingProps } from './types';

/**
 * EWM-942 (AC-6, AC-7, AC-8) — the preflight step.
 *
 * Shown ONLY when the server could not identify the product-code and/or
 * quantity column (AC-9: a file whose columns were both recognised never sees
 * this). It lists every column the file has with its heading and a couple of
 * real values, so a customer whose columns are called "Thing" and "How many"
 * can tell them apart by their contents, then pick them.
 *
 * Nothing is parsed into order lines until Confirm: the file is re-sent to the
 * BFF with the chosen mapping. Cancel abandons the upload outright — it does
 * not fall back to a best guess, which would be exactly the silent misreading
 * AC-13 forbids.
 */
const columnLetter = (index: number): string => {
  let letter = '';
  let remaining = index;
  do {
    letter = String.fromCharCode(65 + (remaining % 26)) + letter;
    remaining = Math.floor(remaining / 26) - 1;
  } while (remaining >= 0);
  return letter;
};

/** The quantity select's "my file has no quantity column" option. */
const NO_QUANTITY_COLUMN = 'none';

const ColumnMapping = ({ columns, headerRowIndex, fileName, onConfirm, onCancel }: ColumnMappingProps) => {
  const translate = useTranslations();
  const baseId = useId();
  const skuSelectId = `${baseId}-sku`;
  const quantitySelectId = `${baseId}-quantity`;
  const errorId = `${baseId}-error`;

  const [skuValue, setSkuValue] = useState('');
  const [quantityValue, setQuantityValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  // PR #1176 review (suggestion 5) — which field the current error is about, so
  // the offending select can be marked `aria-invalid` and focus moved to it. A
  // screen-reader user otherwise hears the message without learning which of
  // the two controls to fix. The distinctness rule is the SKU's to resolve (the
  // customer picks the quantity, then discovers it clashes), so it points there.
  const [invalidField, setInvalidField] = useState<'sku' | 'quantity' | null>(null);
  const skuSelectRef = useRef<HTMLSelectElement>(null);
  const quantitySelectRef = useRef<HTMLSelectElement>(null);

  /**
   * PR #1189 round-10 review (suggestion 1) — the drawer BODY swaps to this step
   * while focus stays wherever the async parse left it, so a screen-reader user
   * is not told the upload became a question. The shared `Drawer` already gives
   * us `role="dialog"`, `aria-modal`, Esc, a focus trap and focus-return; what
   * was missing is the step-change announcement.
   *
   * Focus moves to the first field rather than the heading, matching the
   * in-repo precedent for a drawer step change (`registration-drawer-content`
   * does `setFocus('companyName')` on step 1 -> 2). The select carries its own
   * label, so the customer hears what is being asked and where they are.
   */
  useEffect(() => {
    skuSelectRef.current?.focus();
  }, []);

  const optionLabel = (column: { index: number; label: string }) =>
    column.label
      ? translate('quick-order.mapping-column-option-labelled', {
          label: column.label,
          letter: columnLetter(column.index),
        })
      : translate('quick-order.mapping-column-option-unlabelled', { letter: columnLetter(column.index) });

  /**
   * Required-ness and the cross-field rule live here, and the submit is gated on
   * what this returns — the UI never marks a field required without a check
   * behind it.
   */
  const validate = (): { headerRowIndex: number; skuColumn: number; quantityColumn: number | null } | null => {
    if (skuValue === '') {
      setError(translate('quick-order.mapping-error-sku-required'));
      setInvalidField('sku');
      skuSelectRef.current?.focus();
      return null;
    }
    if (quantityValue === '') {
      setError(translate('quick-order.mapping-error-quantity-required'));
      setInvalidField('quantity');
      quantitySelectRef.current?.focus();
      return null;
    }
    if (quantityValue !== NO_QUANTITY_COLUMN && quantityValue === skuValue) {
      setError(translate('quick-order.mapping-error-distinct'));
      setInvalidField('sku');
      skuSelectRef.current?.focus();
      return null;
    }
    setError(null);
    setInvalidField(null);
    return {
      headerRowIndex,
      skuColumn: Number(skuValue),
      quantityColumn: quantityValue === NO_QUANTITY_COLUMN ? null : Number(quantityValue),
    };
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const mapping = validate();
    if (mapping) onConfirm(mapping);
  };

  return (
    <form onSubmit={handleSubmit} className="flex min-h-full flex-col">
      <div className="space-y-5 p-5">
        <div>
          <h3 className="text-16 text-primary font-bold">{translate('quick-order.mapping-title')}</h3>
          <p className="text-13 mt-2 text-gray-600">{translate('quick-order.mapping-desc')}</p>
          {fileName && <p className="text-12 mt-1 text-gray-500">{fileName}</p>}
        </div>

        {/* AC-7 — every column with a couple of its real values, so the customer
            can tell which is which without opening the file again. The drawer is
            narrow, so the table scrolls horizontally inside its own container
            rather than pushing the page sideways. */}
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="text-12 px-3 py-2 text-left font-semibold text-gray-600">
                  {translate('quick-order.mapping-column-heading')}
                </th>
                <th scope="col" className="text-12 px-3 py-2 text-left font-semibold text-gray-600">
                  {translate('quick-order.mapping-header-heading')}
                </th>
                <th scope="col" className="text-12 px-3 py-2 text-left font-semibold text-gray-600">
                  {translate('quick-order.mapping-samples-heading')}
                </th>
              </tr>
            </thead>
            <tbody>
              {columns.map((column) => (
                <tr key={column.index} className="border-t border-gray-100">
                  <th scope="row" className="text-13 px-3 py-2 text-left font-semibold text-gray-700">
                    {columnLetter(column.index)}
                  </th>
                  <td className="text-13 px-3 py-2 text-gray-800">
                    {column.label || (
                      <span className="text-gray-400">{translate('quick-order.mapping-no-heading')}</span>
                    )}
                  </td>
                  <td className="text-13 px-3 py-2 text-gray-600">
                    {column.samples.length > 0 ? (
                      column.samples.join(', ')
                    ) : (
                      <span className="text-gray-400">{translate('quick-order.mapping-no-samples')}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor={skuSelectId} className="text-13 mb-1 block font-semibold text-gray-700">
              {translate('quick-order.mapping-sku-label')}
            </label>
            <select
              id={skuSelectId}
              ref={skuSelectRef}
              value={skuValue}
              onChange={(event) => {
                setSkuValue(event.target.value);
                if (invalidField === 'sku') setInvalidField(null);
              }}
              aria-describedby={error ? errorId : undefined}
              aria-invalid={invalidField === 'sku'}
              className={`text-14 w-full rounded-lg border px-3 py-2.5 text-gray-800 ${
                invalidField === 'sku' ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">{translate('quick-order.mapping-choose')}</option>
              {columns.map((column) => (
                <option key={column.index} value={String(column.index)}>
                  {optionLabel(column)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor={quantitySelectId} className="text-13 mb-1 block font-semibold text-gray-700">
              {translate('quick-order.mapping-quantity-label')}
            </label>
            <select
              id={quantitySelectId}
              ref={quantitySelectRef}
              value={quantityValue}
              onChange={(event) => {
                setQuantityValue(event.target.value);
                if (invalidField) setInvalidField(null);
              }}
              aria-describedby={error ? errorId : undefined}
              aria-invalid={invalidField === 'quantity'}
              className={`text-14 w-full rounded-lg border px-3 py-2.5 text-gray-800 ${
                invalidField === 'quantity' ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">{translate('quick-order.mapping-choose')}</option>
              {columns.map((column) => (
                <option key={column.index} value={String(column.index)}>
                  {optionLabel(column)}
                </option>
              ))}
              {/* A file that is just a list of codes is a real and valid shape —
                  EWM-949 accepts it on the CSV path at a quantity of 1. Without
                  this option a codes-only spreadsheet would reach a preflight it
                  could never complete. */}
              <option value={NO_QUANTITY_COLUMN}>{translate('quick-order.mapping-quantity-none')}</option>
            </select>
          </div>

          {error && (
            <p id={errorId} role="alert" className="text-13 font-medium text-red-600">
              {error}
            </p>
          )}
        </div>
      </div>

      <div className="sticky bottom-0 mt-auto space-y-3 border-t border-gray-200 bg-white p-5">
        <Button type="submit" size="m" variant="primary" className="w-full !rounded-lg !px-5 !font-semibold">
          {translate('quick-order.mapping-confirm')}
        </Button>
        {/* AC-8 — cancelling abandons the upload. It is a button, not a link:
            nothing is navigated to, and nothing is processed. */}
        <button
          type="button"
          onClick={onCancel}
          className="text-14 text-primary block w-full rounded-lg border border-gray-200 px-5 py-3 font-semibold transition-colors hover:bg-gray-50"
        >
          {translate('quick-order.mapping-cancel')}
        </button>
      </div>
    </form>
  );
};

export default ColumnMapping;
